import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Badge,
  Button,
  Form,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Col,
  Table,
} from "react-bootstrap";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaFolder,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import { catalogApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import { useAuth } from "../context/AuthContext";
import FormField from "../components/forms/FormField";
import { CategoryAutocomplete } from "../components/CategoryAutocomplete";
import {
  flattenCategoryOptions,
  getDescendantIds,
} from "../utils/categories";
import { buildPageItems } from "../utils/adminOrders";
import {
  CATEGORY_PAGE_SIZES,
  CATEGORY_SORT_OPTIONS,
  enrichCategoryRows,
  filterAndSortCategories,
  paginateItems,
} from "../utils/adminCategories";
import { TableSkeleton } from "../components/Skeleton";
import "./AdminProductForm.css";
import "./AdminCategories.css";
import "./dashboard/ManagePages.css";

const categorySchema = Yup.object({
  name: Yup.string().trim().required("Name is required").min(2).max(120),
  parent: Yup.string().nullable(),
  sortOrder: Yup.number().integer().min(0),
  isActive: Yup.boolean(),
  description: Yup.string().max(500),
});

const emptyForm = {
  name: "",
  parent: "",
  sortOrder: 0,
  isActive: true,
  description: "",
};

const DEFAULT_FILTERS = {
  search: "",
  status: "all",
  level: "all",
  sortBy: "tree",
};

const AdminCategories = () => {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createParentId, setCreateParentId] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await catalogApi.adminCategories();
      setCategories(res.data.data.categories || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load categories"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== "admin") return;
    load();
  }, [isAuthenticated, user, load]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) return prev;
        setPage(1);
        return { ...prev, search: searchInput };
      });
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const flatOptions = useMemo(
    () => flattenCategoryOptions(categories),
    [categories]
  );

  const enrichedRows = useMemo(
    () => enrichCategoryRows(flatOptions, categories),
    [flatOptions, categories]
  );

  const filteredRows = useMemo(
    () => filterAndSortCategories(enrichedRows, filters),
    [enrichedRows, filters]
  );

  const pagination = useMemo(
    () => paginateItems(filteredRows, page, pageSize),
    [filteredRows, page, pageSize]
  );

  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  const parentOptions = useMemo(() => {
    if (!editing) return flatOptions;
    const blocked = new Set([
      String(editing._id),
      ...getDescendantIds(editing._id, categories),
    ]);
    return flatOptions.filter((o) => !blocked.has(String(o.id)));
  }, [flatOptions, editing, categories]);

  const hasActiveFilters =
    Boolean(filters.search.trim()) ||
    filters.status !== "all" ||
    filters.level !== "all" ||
    filters.sortBy !== "tree";

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput("");
    setPage(1);
  };

  const openCreate = (parentId = "") => {
    setEditing(null);
    setCreateParentId(parentId || "");
    setShowModal(true);
  };

  const openEdit = (categoryOrRow) => {
    const raw =
      categoryOrRow?.raw ||
      categories.find(
        (c) => String(c._id) === String(categoryOrRow._id || categoryOrRow.id)
      ) ||
      categoryOrRow;
    setCreateParentId("");
    setEditing(raw);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setCreateParentId("");
  };

  const handleDelete = async (row) => {
    const id = row.id || row._id;
    const hasChildren = flatOptions.some(
      (o) => String(o.parentId) === String(id)
    );
    if (hasChildren) {
      toast.error("Remove or move subcategories first");
      return;
    }
    if (!window.confirm(`Delete category "${row.name}"?`)) return;
    try {
      await catalogApi.deleteCategory(id);
      toast.success("Category deleted");
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not delete category"));
    }
  };

  const pageItems = buildPageItems(pagination.page, pagination.totalPages);

  if (authLoading) return <TableSkeleton rows={6} cols={7} />;
  if (!isAuthenticated || user?.role !== "admin") {
    return (
      <Navigate to="/login" state={{ from: "/dashboard/categories" }} replace />
    );
  }

  const isEditMode = Boolean(editing?._id);
  const initialValues = isEditMode
    ? {
        name: editing.name || "",
        parent: editing.parent?._id || editing.parent || "",
        sortOrder: editing.sortOrder ?? 0,
        isActive: editing.isActive !== false,
        description: editing.description || "",
      }
    : {
        ...emptyForm,
        parent: createParentId || "",
      };

  const topCount = enrichedRows.filter((r) => r.depth === 0).length;
  const subCount = enrichedRows.length - topCount;

  return (
    <main className="admin-product-page">
      <div className="admin-product-header">
        <div>
          <span className="admin-badge">Admin</span>
          <h1>Categories</h1>
          <p>
            Create and manage categories, subcategories, and nested levels used
            in search and product forms.
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <span className="manage-count">
            {enrichedRows.length} total · {topCount} top · {subCount} sub
          </span>
          <Button as={Link} to="/dashboard/products" variant="outline-light">
            Products
          </Button>
          <Button className="admin-save-btn" onClick={() => openCreate("")}>
            <FaPlus /> Add category
          </Button>
        </div>
      </div>

      <section className="admin-list-page">
        <div className="manage-toolbar">
          <InputGroup className="manage-search">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search name, slug, parent, path…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>

          <Form.Select
            className="manage-filter"
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>

          <Form.Select
            className="manage-filter"
            value={filters.level}
            onChange={(e) => updateFilter("level", e.target.value)}
          >
            <option value="all">All levels</option>
            <option value="top">Top-level only</option>
            <option value="sub">Subcategories only</option>
          </Form.Select>

          <Form.Select
            className="manage-filter"
            value={filters.sortBy}
            onChange={(e) => updateFilter("sortBy", e.target.value)}
          >
            {CATEGORY_SORT_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter manage-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {CATEGORY_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Form.Select>

          {hasActiveFilters && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="manage-clear-btn"
              onClick={clearFilters}
            >
              <FaTimes /> Clear
            </Button>
          )}
        </div>

        {loading ? (
          <TableSkeleton rows={8} cols={7} />
        ) : enrichedRows.length === 0 ? (
          <div className="manage-empty">
            <p>No categories yet. Add your first category to get started.</p>
            <Button className="admin-save-btn" onClick={() => openCreate("")}>
              <FaPlus /> Add category
            </Button>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="manage-empty">
            <p>No categories match your search or filters.</p>
            <Button variant="link" onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        ) : (
          <>
            <div className="manage-card table-responsive">
              <Table hover className="manage-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th className="d-none d-md-table-cell">Slug</th>
                    <th className="d-none d-lg-table-cell">Parent</th>
                    <th className="d-none d-sm-table-cell">Order</th>
                    <th>Status</th>
                    <th>Level</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {pagination.items.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <span
                          className="cat-name"
                          style={{ paddingLeft: `${Math.min(row.depth, 6) * 14}px` }}
                        >
                          <FaFolder className="me-2" />
                          {row.name}
                        </span>
                        {row.depth > 0 && (
                          <div className="manage-muted d-lg-none">
                            {row.path.slice(0, -1).join(" › ")}
                          </div>
                        )}
                      </td>
                      <td className="d-none d-md-table-cell">
                        <code>{row.slug}</code>
                      </td>
                      <td className="d-none d-lg-table-cell">
                        {row.depth > 0
                          ? row.path[row.path.length - 2]
                          : "—"}
                      </td>
                      <td className="d-none d-sm-table-cell">{row.sortOrder}</td>
                      <td>
                        {row.isActive ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td>
                        <Badge bg={row.depth === 0 ? "dark" : "light"} text={row.depth === 0 ? undefined : "dark"}>
                          {row.depth === 0 ? "Top" : `L${row.depth + 1}`}
                        </Badge>
                      </td>
                      <td className="text-nowrap manage-row-actions">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openCreate(row.id)}
                          title="Add subcategory"
                        >
                          <FaPlus />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openEdit(row)}
                          title="Edit"
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(row)}
                          title="Delete"
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="manage-pagination-bar">
              <p className="manage-pagination-meta">
                Showing {pagination.showingFrom}–{pagination.showingTo} of{" "}
                {pagination.total} categories
                {hasActiveFilters ? ` (filtered from ${enrichedRows.length})` : ""}
              </p>
              {pagination.totalPages > 1 && (
                <Pagination className="manage-pagination mb-0">
                  <Pagination.Prev
                    disabled={pagination.page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  />
                  {pageItems.map((item, index) =>
                    item === "…" ? (
                      <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                    ) : (
                      <Pagination.Item
                        key={item}
                        active={item === pagination.page}
                        onClick={() => setPage(item)}
                      >
                        {item}
                      </Pagination.Item>
                    )
                  )}
                  <Pagination.Next
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() =>
                      setPage((p) => Math.min(pagination.totalPages, p + 1))
                    }
                  />
                </Pagination>
              )}
            </div>
          </>
        )}
      </section>

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Edit category" : "Add category"}
          </Modal.Title>
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={categorySchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const payload = {
                name: values.name.trim(),
                parent: values.parent || null,
                sortOrder: Number(values.sortOrder) || 0,
                isActive: values.isActive,
                description: values.description || "",
              };
              if (isEditMode) {
                await catalogApi.updateCategory(editing._id, payload);
                toast.success("Category updated");
              } else {
                await catalogApi.createCategory(payload);
                toast.success("Category created");
              }
              closeModal();
              load();
            } catch (error) {
              toast.error(getErrorMessage(error, "Could not save category"));
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <FormikForm>
              <Modal.Body>
                <FormField
                  name="name"
                  label="Category name"
                  placeholder="e.g. Box Files"
                />
                <CategoryAutocomplete
                  label="Parent category"
                  options={parentOptions}
                  value={values.parent || ""}
                  onChange={(id) => setFieldValue("parent", id || "")}
                  placeholder="Type to search, or leave empty for top level..."
                  helpText="Choose a parent to create a subcategory / nested category. Clear for top level."
                />
                <Row>
                  <Col md={6}>
                    <FormField name="sortOrder" type="number" label="Sort order" />
                  </Col>
                  <Col md={6} className="d-flex align-items-end mb-3">
                    <Form.Check
                      type="switch"
                      id="cat-active"
                      label="Active"
                      checked={values.isActive}
                      onChange={(e) =>
                        setFieldValue("isActive", e.target.checked)
                      }
                    />
                  </Col>
                </Row>
                <FormField
                  name="description"
                  type="textarea"
                  rows={2}
                  label="Description (optional)"
                />
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={closeModal} type="button">
                  Cancel
                </Button>
                <Button
                  className="admin-save-btn"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save category"}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>
    </main>
  );
};

export default AdminCategories;
